import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw, Loader2, CheckCircle2, AlertTriangle, CreditCard, ExternalLink, Copy, RotateCw,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Line = ({ label, value, testid, tone }) => (
  <div className="flex items-baseline gap-2 py-1 border-b border-[#F4F2ED] last:border-0">
    <span className="text-[11px] uppercase tracking-[0.08em] text-[#8A857A] w-[170px] shrink-0">{label}</span>
    <span className="text-[12.5px] break-all" style={{ color: tone || "#1A1A1A" }} data-testid={testid}>{value}</span>
  </div>
);

/* Stripe is the source of truth for money, so "which Stripe account is this
   environment actually talking to" has to be answerable without an SSH session.
   Nothing secret is shown — the key is only ever identified by its last 4. */
export default function BillingConnectionCard() {
  const [h, setH] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/billing/connection`);
      setH(r.data);
    } catch (e) {
      setH({ ready: false, reason: e?.response?.data?.detail || "Could not read the billing connection" });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/billing/sync`);
      toast.success(`Read back from Stripe (${r.data.mode})`, {
        description: `${r.data.customers} customers · ${r.data.subscriptions} subscriptions · ${r.data.invoices} invoices`,
      });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Stripe sync failed"); }
    finally { setSyncing(false); }
  };

  const ready = !!h?.ready;
  const mode = h?.mode;
  const isPreview = typeof window !== "undefined"
    && window.location.hostname.includes("preview.emergentagent.com");
  // The whole point of the card: a production site still holding the test key.
  const wrongKeyHere = !isPreview && mode === "test";
  const webhookUrl = typeof window !== "undefined"
    ? `${window.location.origin}${h?.webhook_path || "/api/billing/webhook/stripe"}`
    : "";

  const copy = () => {
    navigator.clipboard?.writeText(webhookUrl);
    toast.success("Webhook URL copied");
  };

  return (
    <Card className="bg-white border-[#DAD5CA]" data-testid="billing-connection-card">
      <CardContent className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <CreditCard className="w-4 h-4 text-[#59554D] mt-0.5 shrink-0" />
            <div className="min-w-0">
              <h3 className="text-[15px] font-bold text-[#1A1A1A]">Billing — Stripe connection</h3>
              <p className="text-[12.5px] text-[#59554D]">
                Which Stripe account this environment reads, and whether payment events can reach us.
                Keys live in the server environment, never in the database — so this is read-only.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Button size="sm" variant="outline" onClick={sync} disabled={syncing || !ready}
              className="h-8 border-[#DAD5CA] text-[#59554D]" data-testid="billing-health-sync">
              {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
              <span className="ml-1.5 text-[12px]">Sync now</span>
            </Button>
            <Button size="sm" variant="outline" onClick={load} disabled={loading}
              className="h-8 border-[#DAD5CA] text-[#59554D]" data-testid="billing-health-refresh">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="billing-health-state">
          {ready ? (
            <span className="inline-flex items-center gap-1.5 mono text-[11px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: "#E3F3E9", border: "1px solid #A9CFBB", color: "#0E6B3F" }}>
              <CheckCircle2 className="w-3 h-3" /> CONNECTED
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 mono text-[11px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: "#FFF8D6", border: "1px solid #E8C86A", color: "#6B5600" }}>
              <AlertTriangle className="w-3 h-3" /> NOT CONNECTED
            </span>
          )}
          {mode && (
            <span className="inline-flex items-center gap-1.5 mono text-[11px] font-bold px-2 py-0.5 rounded-full"
              data-testid="billing-health-mode"
              style={mode === "live"
                ? { background: "#E3F3E9", border: "1px solid #A9CFBB", color: "#0E6B3F" }
                : { background: "#EFEBE2", border: "1px solid #DAD5CA", color: "#59554D" }}>
              {mode === "live" ? "LIVE MODE" : "TEST MODE (SANDBOX)"}
            </span>
          )}
          {!h?.webhook_secret_set && (
            <span className="text-[11.5px] text-[#8A6D00]" data-testid="billing-health-nowhsec">
              No webhook signing secret — payment events are being refused
            </span>
          )}
        </div>

        {wrongKeyHere && (
          <div className="mt-2 rounded-lg border border-[#E8C86A] bg-[#FFF8D6] p-2.5"
               data-testid="billing-health-wrong-mode">
            <p className="text-[12.5px] font-semibold text-[#6B5600]">
              This is the live site but it is running the TEST key.
            </p>
            <p className="text-[12px] text-[#6B5600] mt-1">
              The <span className="mono">STRIPE_SECRET_KEY</span> secret has not reached the server, so no
              real customers, subscriptions or invoices will ever appear. Add it as a GitHub Actions secret
              (a <em>secret</em>, not a variable, and not inside an Environment the deploy job does not use),
              then redeploy — the deploy log should print "✓ STRIPE_SECRET_KEY synced from GitHub secret".
            </p>
          </div>
        )}

        {h?.reason && !ready && (
          <div className="mt-2 rounded-lg border border-[#E8C86A] bg-[#FFF8D6] p-2.5"
               data-testid="billing-health-reason">
            <p className="text-[12.5px] text-[#6B5600]">{h.reason}</p>
          </div>
        )}

        <div className="mt-3">
          <Line label="Stripe account"
            value={h?.account
              ? [h.account.name, h.account.id, h.account.country, h.account.default_currency]
                  .filter(Boolean).join(" · ")
              : "unknown until the key is accepted"}
            testid="billing-health-account" />
          <Line label="Secret key"
            value={h?.key_configured ? `set · ends ${h.key_last4}` : "NOT SET — billing is disconnected"}
            tone={h?.key_configured ? undefined : "#6B5600"}
            testid="billing-health-key" />
          <Line label="Webhook secret"
            value={h?.webhook_secret_set ? "set" : "NOT SET — events are refused with 503"}
            tone={h?.webhook_secret_set ? undefined : "#6B5600"}
            testid="billing-health-whsec" />
          <Line label="Docket invoices"
            value={h?.docket_invoices_draft
              ? "raised as a DRAFT for review in Stripe — nothing is emailed"
              : "finalised and emailed to the client straight away"}
            testid="billing-health-draft" />
          <Line label="Last read from Stripe"
            value={h?.last_sync_at ? new Date(h.last_sync_at).toLocaleString() : "never"}
            testid="billing-health-sync-at" />
          <Line label="Mirrored here"
            value={h?.mirror
              ? `${h.mirror.customers} customers · ${h.mirror.subscriptions} subscriptions · ${h.mirror.invoices} invoices`
              : "—"}
            testid="billing-health-mirror" />
        </div>

        <div className="mt-3 rounded-lg border border-[#DAD5CA] bg-[#FBFAF7] p-2.5">
          <div className="text-[11px] uppercase tracking-[0.08em] text-[#8A857A]">Webhook endpoint for Stripe</div>
          <div className="flex items-center gap-2 mt-1">
            <span className="mono text-[12px] text-[#1A1A1A] break-all" data-testid="billing-health-webhook-url">
              {webhookUrl}
            </span>
            <button type="button" onClick={copy} title="Copy" data-testid="billing-copy-webhook"
                    className="shrink-0 p-1 rounded hover:bg-[#EFEBE2]">
              <Copy className="w-3.5 h-3.5 text-[#59554D]" />
            </button>
          </div>
          <p className="text-[12px] text-[#59554D] mt-1.5">
            Stripe endpoints are per mode, so the live account needs its own. Subscribe it to the
            <span className="mono"> invoice.*</span> and <span className="mono">customer.subscription.*</span> events,
            then put its <span className="mono">whsec_…</span> in the
            <span className="mono"> STRIPE_WEBHOOK_SECRET</span> secret.
          </p>
        </div>

        {h?.dashboard_base && (
          <a href={h.dashboard_base} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1 mt-3 text-[12px] font-semibold text-[#59554D] underline"
             data-testid="billing-health-dashboard-link">
            Open this Stripe dashboard <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </CardContent>
    </Card>
  );
}
